/**
 * Datalab 域默认 HTTP 入口：解包内层 data，统一错误提示与 token 失效处理。
 *
 * iframe guest token 见 `iframeToken/`。
 * @see iframeToken · types.ts · TResponseError
 */
import http from '@/utils/http';
import { showErrorMessagetip } from '@/pages/home/components/ErrorMessageTip/ErrorMessageTip';
import { getLocale } from 'umi';
import { getTokenFailureUrlValid } from './requestUtils';
import type { TDatalabHttpConfig } from './types';

/**
 * 从 reject 载荷中读取业务错误码（兼容 TResponseError 及仅含 code 的简形）。
 */
const getBusinessErrorCode = (info: unknown): number | undefined => {
  if (!info || typeof info !== 'object') {
    return undefined;
  }
  const { code } = info as { code?: unknown };
  return typeof code === 'number' ? code : undefined;
};

/**
 * 从 reject 载荷中读取业务错误文案。
 */
const getBusinessErrorMessage = (info: unknown): string | undefined => {
  if (!info || typeof info !== 'object') {
    return undefined;
  }
  const { msg } = info as { msg?: unknown };
  return typeof msg === 'string' ? msg : undefined;
};

/**
 * 判断是否为「requestId 过期」类错误（-998），此类错误不应再 reject 给上层。
 */
const shouldAbortPendingRequestError = (info: unknown): boolean =>
  getBusinessErrorCode(info) === -998;

/**
 * Datalab 域默认 HTTP 入口：解包内层 data，统一错误提示与 token 失效处理。
 *
 * 失败时 reject 的载荷在业务侧通常按 TResponseError 消费；此处入参为 unknown，
 * 因全局 http 拦截器还可能 reject 网络错误、完整 axios 响应等非 TResponseError 形态。
 */
const datalabHttp = <TData = unknown>(
  config: TDatalabHttpConfig,
): Promise<TData> =>
  new Promise<TData>((resolve, reject) => {
    http({ ...config, errorHandler: true }).then(
      (data: unknown) => {
        resolve(data as TData);
      },
      (err: unknown) => {
        const errorCode = getBusinessErrorCode(err);
        if (errorCode === 4001) {
          getTokenFailureUrlValid();
        }
        if (shouldAbortPendingRequestError(err)) {
          return;
        }
        if ((!errorCode || errorCode === 999) && !config.useCustomTips) {
          showErrorMessagetip({
            errorCode: 999,
            content:
              getBusinessErrorMessage(err) ||
              (getLocale() === 'en-US' ? 'System error' : '系统错误'),
            duration: 3000,
            isClose: true,
            type: 'error',
          });
        }
        reject(err);
      },
    );
  });

export default datalabHttp;
